import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useParams, Navigate, Link, useLocation } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { Html5Qrcode } from 'html5-qrcode';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/layout/Navbar';
import PageHeader from '../components/layout/PageHeader';
import { canScanCards } from '../lib/membershipAccess';
import {
  ScanLine, CheckCircle, XCircle, Camera, DoorOpen, Beer, Search, Undo2,
  AlertCircle, Users, Ticket, ChevronLeft, CalendarPlus, Loader2,
} from 'lucide-react';
import './SponsorPages.css';
import './ScanCardsPage.css';

/**
 * ScanCardsPage — the volunteer's screen for membership cards (v35).
 *
 * The ONE new page of the membership-card slice, and only people with the
 * capability (or club admins) ever see it. Two taps to the first scan:
 * Wallet → Scan cards (B-46; was a navbar link) → Gate or Bar. The camera
 * opens on that last tap.
 *
 *   Gate mode  — VALID/INVALID + name, one button per ticket price for
 *                non-members, Find by name for forgotten phones, Undo last
 *                ticket. Everything logs against today's home fixture (or an
 *                ad-hoc gate day the scanner opens with one tap).
 *   Bar mode   — VALID/INVALID + name. No amounts. Footfall only.
 *
 * Camera: html5-qrcode, the same library and pattern as SponsorScanPage (so
 * it works in the bundled native builds the same way). After each result
 * the camera pauses for ~2.5s and resumes itself — zero taps per person.
 * The same QR decoded again within 20s is ignored (the member is still
 * holding their phone up), so a fast queue never sees a spurious red.
 */
const RESUME_MS = 2500;
const SAME_CODE_WINDOW_MS = 20000;

// UC-11: this device remembers the last mode (Gate/Bar) per club and badges
// it on the picker. Storage can be blocked (private mode) — never fatal.
const modeKey = (slug) => `marakin.scanMode.${slug}`;
const readLastMode = (slug) => {
  try { const m = window.localStorage.getItem(modeKey(slug)); return m === 'gate' || m === 'bar' ? m : null; } catch { return null; }
};
const saveLastMode = (slug, m) => {
  try { window.localStorage.setItem(modeKey(slug), m); } catch { /* storage blocked */ }
};

const reasonCopy = (reason, clubName) => ({
  lapsed: 'Club membership lapsed',
  not_a_member: 'Not a club member',
  already_used: 'Code already used — ask them to refresh their card',
  expired: 'Code expired — ask them to refresh their card',
  unknown: `Not one of ${clubName || 'this club'}'s cards`,
  // B-66: Marakin staff preview card — refused by the server, never logged.
  staff_preview: 'Marakin staff preview card — not a real card',
}[reason] || 'Not valid');

const gbp = (pence) => `£${(Number(pence || 0) / 100).toFixed(Number(pence || 0) % 100 === 0 ? 0 : 2)}`;
const timeOf = (iso) => new Date(iso).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });

const ScanCardsPage = () => {
  const { clubSlug } = useParams();
  const location = useLocation(); // UC-B5: return path for /login
  const { user, loading: authLoading, getMembership } = useAuth();
  const membership = getMembership(clubSlug);
  const qc = useQueryClient();

  const [mode, setMode] = useState(null);            // null | 'gate' | 'bar'
  const [eventId, setEventId] = useState(null);      // chosen fixture when two share the day
  const [scanning, setScanning] = useState(false);
  const [cameraError, setCameraError] = useState('');
  const [codeInput, setCodeInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);        // last scan outcome
  const [error, setError] = useState('');
  const [findOpen, setFindOpen] = useState(false);
  const [findQuery, setFindQuery] = useState('');
  const [findResults, setFindResults] = useState(null);
  const [tally, setTally] = useState(null);
  const [lastTicket, setLastTicket] = useState(null);
  const [lastMode, setLastMode] = useState(() => readLastMode(clubSlug));
  const [typedOpen, setTypedOpen] = useState(false); // "Can't scan?" fold

  const scannerRef = useRef(null);
  const lastCodeRef = useRef({ text: '', at: 0 });
  const resumeTimerRef = useRef(null);
  const handlingRef = useRef(false);
  const busyRef = useRef(false);
  useEffect(() => { busyRef.current = busy; }, [busy]);

  const { data: club } = useQuery({
    queryKey: ['club', clubSlug],
    queryFn: () => axios.get(`/clubs/${clubSlug}`).then(r => r.data),
  });

  const isAdmin = !!membership && (membership.role === 'admin' || membership.clubAdmin);
  const allowed = canScanCards(membership); // same rule as the Wallet's Scan cards button (B-46)

  // Today's context — prices, home fixtures, gate open?, shared tally. Polled
  // so two phones on the same gate see the same numbers.
  const { data: ctx, refetch: refetchCtx, isError: ctxError } = useQuery({
    queryKey: ['scan-context', clubSlug, eventId],
    queryFn: () => axios.get(`/clubs/${clubSlug}/membership/scan/context`, { params: eventId ? { event_id: eventId } : {} }).then(r => r.data),
    enabled: !!user && allowed,
    refetchInterval: 30000,
  });
  useEffect(() => { if (ctx?.tally) setTally(ctx.tally); }, [ctx?.tally]);

  const fixtures = ctx?.home_fixtures || [];
  const gateOpen = !!ctx?.gate_open;
  const needsFixtureChoice = mode === 'gate' && fixtures.length > 1 && !eventId;
  const targetFixture = fixtures.length === 1 ? fixtures[0] : fixtures.find(f => f.id === eventId) || null;

  // ── Camera ────────────────────────────────────────────────────────────
  const stopScanner = useCallback(async () => {
    const s = scannerRef.current;
    scannerRef.current = null;
    setScanning(false);
    clearTimeout(resumeTimerRef.current);
    // QA #1: a Switch during the 2.5s pause used to leave handlingRef stuck
    // true — the next camera opened deaf. Reset every decode gate here.
    handlingRef.current = false;
    lastCodeRef.current = { text: '', at: 0 };
    if (s) { try { await s.stop(); s.clear(); } catch { /* already stopped */ } }
  }, []);
  useEffect(() => () => { stopScanner(); }, [stopScanner]);

  const pauseThenResume = useCallback(() => {
    const s = scannerRef.current;
    if (!s) { handlingRef.current = false; return; }
    try { s.pause(true); } catch { /* not running */ }
    clearTimeout(resumeTimerRef.current);
    resumeTimerRef.current = setTimeout(() => {
      try { if (scannerRef.current) scannerRef.current.resume(); } catch { /* stopped meanwhile */ }
      handlingRef.current = false;
    }, RESUME_MS);
  }, []);

  const submitCode = useCallback(async (raw, { typed = false } = {}) => {
    const code = String(raw || '').trim();
    if (!code || busy) return;
    setBusy(true); setError('');
    try {
      const res = await axios.post(`/clubs/${clubSlug}/membership/scan`, { code, mode, event_id: eventId || undefined });
      setResult({ ...res.data, at: Date.now(), typed });
      if (res.data.tally) setTally(res.data.tally);
      if (typed) setCodeInput('');
    } catch (err) {
      const code409 = err.response?.data?.error;
      if (code409 === 'gate_closed' || code409 === 'choose_fixture') { refetchCtx(); }
      setError(err.response?.data?.message || err.response?.data?.error || 'Could not check that card — try again.');
    } finally {
      setBusy(false);
    }
  }, [busy, clubSlug, mode, eventId, refetchCtx]);

  const submitRef = useRef(submitCode);
  useEffect(() => { submitRef.current = submitCode; }, [submitCode]);

  const startScanner = useCallback(() => {
    if (scannerRef.current) return; // a double-tap must not open two cameras on one div
    setCameraError('');
    setScanning(true);
    handlingRef.current = false;
    setTimeout(async () => {
      if (scannerRef.current) return;
      try {
        const scanner = new Html5Qrcode('card-viewfinder');
        scannerRef.current = scanner;
        await scanner.start(
          { facingMode: 'environment' },
          { fps: 10, qrbox: { width: 240, height: 240 } },
          (decodedText) => {
            if (handlingRef.current || busyRef.current) return; // a typed check / ticket tap is in flight — try again next frame
            const now = Date.now();
            // Same phone still held up → ignore, don't flash red.
            if (decodedText === lastCodeRef.current.text && now - lastCodeRef.current.at < SAME_CODE_WINDOW_MS) return;
            handlingRef.current = true;
            lastCodeRef.current = { text: decodedText, at: now };
            submitRef.current(decodedText).finally(pauseThenResume);
          },
          () => { /* per-frame misses — normal */ }
        );
      } catch {
        scannerRef.current = null;
        setScanning(false);
        setTypedOpen(true); // keep the code box open across camera retries
        setCameraError('Camera not available — check the browser has camera permission, or type the code from under the QR.');
      }
    }, 60);
  }, [pauseThenResume]);

  // Picking a mode is the second tap — the camera opens on it.
  const chooseMode = (m) => {
    setMode(m); setResult(null); setError(''); setEventId(null); setFindOpen(false);
    saveLastMode(clubSlug, m); setLastMode(m);
    if (m === 'bar' || fixtures.length <= 1) startScanner();
  };
  const chooseFixture = (id) => { setEventId(id); startScanner(); };
  const switchMode = async () => { await stopScanner(); setMode(null); setEventId(null); setResult(null); setError(''); setFindOpen(false); setTypedOpen(false); };

  const openGateDay = async () => {
    setBusy(true); setError('');
    try {
      const res = await axios.post(`/clubs/${clubSlug}/membership/scan/gate-day`);
      qc.setQueryData(['scan-context', clubSlug, eventId], res.data);
    } catch (err) { setError(err.response?.data?.message || 'Could not open the gate for today.'); }
    finally { setBusy(false); }
  };

  // ── Tickets ───────────────────────────────────────────────────────────
  const addTicket = async (label) => {
    if (busy) return;
    setBusy(true); setError('');
    try {
      const res = await axios.post(`/clubs/${clubSlug}/membership/scan/ticket`, { label, event_id: eventId || undefined });
      setTally(res.data.tally);
      setLastTicket({ ...res.data.ticket, at: Date.now() });
      setResult({ ticket: res.data.ticket, at: Date.now() });
    } catch (err) { setError(err.response?.data?.message || err.response?.data?.error || 'Could not add that ticket.'); }
    finally { setBusy(false); }
  };
  const undoTicket = async () => {
    if (busy) return;
    setBusy(true); setError('');
    try {
      const res = await axios.post(`/clubs/${clubSlug}/membership/scan/ticket/undo`);
      setTally(res.data.tally);
      setLastTicket(null);
      setResult({ undone: res.data.undone, at: Date.now() });
    } catch (err) {
      // UC-11: past the server's 5-minute undo window → hide Undo, don't let it fail on every tap
      if (err.response?.status === 404) setLastTicket(null);
      setError(err.response?.data?.error || 'Nothing to undo.');
    }
    finally { setBusy(false); }
  };

  // ── Find by name ──────────────────────────────────────────────────────
  useEffect(() => {
    if (!findOpen) return undefined;
    const q = findQuery.trim();
    if (q.length < 2) { setFindResults(null); return undefined; }
    const t = setTimeout(() => {
      axios.get(`/clubs/${clubSlug}/membership/scan/members`, { params: { q } })
        .then(r => setFindResults(r.data))
        .catch(() => setFindResults([]));
    }, 250);
    return () => clearTimeout(t);
  }, [findOpen, findQuery, clubSlug]);

  const admitByName = async (m) => {
    if (busy) return;
    setBusy(true); setError('');
    try {
      const res = await axios.post(`/clubs/${clubSlug}/membership/scan/manual`, { club_member_id: m.club_member_id, mode, event_id: eventId || undefined });
      setResult({ ...res.data, at: Date.now() });
      if (res.data.tally) setTally(res.data.tally);
      setFindOpen(false); setFindQuery(''); setFindResults(null);
    } catch (err) { setError(err.response?.data?.message || err.response?.data?.error || 'Could not admit that member.'); }
    finally { setBusy(false); }
  };

  // ── Gates ─────────────────────────────────────────────────────────────
  if (authLoading) return <div className="page-loading"><div className="spinner" style={{ width: 40, height: 40 }} /></div>;
  if (!user) return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  if (!membership || !allowed) return <Navigate to={`/${clubSlug}`} replace />;

  const modeLabel = mode === 'gate' ? 'Gate' : 'Bar';
  const loggingAgainst = mode === 'gate'
    ? (targetFixture ? `${targetFixture.title}${targetFixture.starts_at ? ` · ${timeOf(targetFixture.starts_at)}` : ''}` : (ctx?.gate_day_open ? 'Gate day (no fixture)' : ''))
    : 'Today'; // WT-20: the subtitle already says "Bar ·" (modeLabel)

  return (
    <div className="sponsor-page scan-cards-page">
      <Navbar club={club} />
      <PageHeader
        icon={ScanLine}
        title="Scan"
        subtitle={mode ? <>{modeLabel} · {loggingAgainst}</> : <>Membership cards · {club?.name || clubSlug}</>}
      >
        {mode ? (
          <button className="btn btn-ghost btn-sm" onClick={switchMode}><ChevronLeft size={14} /> Switch</button>
        ) : (
          <Link to={`/${clubSlug}/wallet`} className="btn btn-ghost btn-sm"><ChevronLeft size={14} /> Wallet</Link>
        )}
      </PageHeader>

      <div className="sponsor-content scan-content">
        {ctxError && (
          <div className="scan-error"><AlertCircle size={16} /> Couldn't load today's gate details. Check your connection and try again.</div>
        )}

        {/* ── Mode picker (once per shift) ── */}
        {!mode && ctx && (
          <div className="sc-modes">
            <button
              className={`sc-mode card ${!gateOpen ? 'is-off' : ''} ${lastMode === 'gate' && gateOpen ? 'is-last' : ''}`}
              disabled={!gateOpen || busy}
              onClick={() => chooseMode('gate')}
              data-tour="scan-gate"
            >
              {lastMode === 'gate' && gateOpen && <em className="sc-last-badge">Last used</em>}
              <DoorOpen size={34} />
              <strong>Gate</strong>
              <span>{gateOpen
                ? (fixtures.length ? `${fixtures.length === 1 ? fixtures[0].title : `${fixtures.length} home fixtures`} today` : 'Gate day — open')
                : 'No home fixture today'}</span>
            </button>
            <button className={`sc-mode card ${lastMode === 'bar' ? 'is-last' : ''}`} disabled={busy} onClick={() => chooseMode('bar')} data-tour="scan-bar">
              {lastMode === 'bar' && <em className="sc-last-badge">Last used</em>}
              <Beer size={34} />
              <strong>Bar</strong>
              <span>Club members only — footfall, no amounts</span>
            </button>
            {!gateOpen && (
              <div className="sc-gateday card">
                <p>Cup game added late, a festival, a sevens day? You can run the gate anyway — scans and tickets log against today's date instead of a fixture.</p>
                <button className="btn btn-secondary" onClick={openGateDay} disabled={busy}>
                  <CalendarPlus size={15} /> Open the gate for today
                </button>
              </div>
            )}
            {error && <div className="scan-error"><XCircle size={16} /> {error}</div>}
          </div>
        )}
        {!mode && !ctx && !ctxError && <div className="page-loading" style={{ minHeight: '30vh' }}><div className="spinner" /></div>}

        {/* ── Two home fixtures on one day — pick once ── */}
        {mode === 'gate' && needsFixtureChoice && (
          <div className="card sc-fixture-pick">
            <h2>Which gate are you on?</h2>
            <p>Two home fixtures today — pick the one this gate is for.</p>
            <div className="sc-fixture-list">
              {fixtures.map(f => (
                <button key={f.id} className="btn btn-secondary" onClick={() => chooseFixture(f.id)}>
                  {f.title} · {timeOf(f.starts_at)}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* ── Scanning surface ── */}
        {mode && !needsFixtureChoice && (
          <div className="sc-work">
            {/* Result banner — stays until the next scan */}
            {result && (
              <div
                key={result.at}
                className={`sc-result ${result.ticket || result.undone ? 'is-ticket' : result.valid ? 'is-valid' : 'is-invalid'}`}
                role="status"
                aria-live="polite"
              >
                {result.ticket && (<><Ticket size={28} /><div><div className="sc-result-head">{result.ticket.label} ticket</div><div className="sc-result-sub">{gbp(result.ticket.pence)} · added to today's tally</div></div></>)}
                {result.undone && (<><Undo2 size={28} /><div><div className="sc-result-head">Undone</div><div className="sc-result-sub">{result.undone.label} ticket removed</div></div></>)}
                {!result.ticket && !result.undone && result.valid && (
                  <>
                    <CheckCircle size={34} />
                    <div>
                      <div className="sc-result-head">VALID</div>
                      <div className="sc-result-name">{result.name}</div>
                      <div className="sc-result-sub">
                        {result.manual ? 'Admitted by name' : 'Club member card'}
                        {result.already_seen_at ? ` · already through at ${timeOf(result.already_seen_at)}` : ''}
                      </div>
                    </div>
                  </>
                )}
                {!result.ticket && !result.undone && !result.valid && (
                  <>
                    <XCircle size={34} />
                    <div>
                      <div className="sc-result-head">INVALID</div>
                      {result.name && <div className="sc-result-name">{result.name}</div>}
                      <div className="sc-result-sub">{reasonCopy(result.reason, club?.name)}</div>
                    </div>
                  </>
                )}
              </div>
            )}

            {/* Camera */}
            <div className="card sc-camera">
              {scanning ? (
                <>
                  <div id="card-viewfinder" className="sc-viewfinder" />
                  <p className="sc-camera-hint">{busy ? <><Loader2 size={13} className="spin-slow" /> Checking…</> : 'Point at the card. It checks itself.'}</p>
                </>
              ) : (
                <>
                  <Camera size={28} style={{ color: 'var(--green)' }} />
                  <button className="btn btn-primary" onClick={startScanner} style={{ width: '100%', justifyContent: 'center', marginTop: 10 }}>
                    <Camera size={16} /> Open camera
                  </button>
                  {cameraError && <div className="scan-error" style={{ marginTop: 10 }}><AlertCircle size={16} /> {cameraError}</div>}
                </>
              )}
              {/* UC-11: the typed code is the fallback, folded under "Can't scan?" (open when the camera failed) */}
              {typedOpen || cameraError ? (
                <div className="sc-typed">
                  <input
                    className="input scan-code-input"
                    placeholder="Type the 10-letter code"
                    aria-label="Card code"
                    autoFocus={typedOpen}
                    value={codeInput}
                    maxLength={12}
                    onChange={e => setCodeInput(e.target.value.toUpperCase())}
                    onKeyDown={e => e.key === 'Enter' && submitCode(codeInput, { typed: true })}
                  />
                  <button className="btn btn-secondary btn-sm" disabled={!codeInput.trim() || busy} onClick={() => submitCode(codeInput, { typed: true })}>Check</button>
                </div>
              ) : (
                <button type="button" className="btn btn-ghost btn-sm sc-cant-scan" onClick={() => setTypedOpen(true)}>
                  Can't scan? Type the code
                </button>
              )}
              {error && <div className="scan-error" style={{ marginTop: 10 }}><XCircle size={16} /> {error}</div>}
            </div>

            {/* Gate-only tools */}
            {mode === 'gate' && (
              <>
                {(ctx?.ticket_prices || []).length > 0 && (
                  <div className="card sc-tickets">
                    <div className="sc-section-head"><Ticket size={15} /> Everyone else at the gate</div>
                    <div className="sc-ticket-grid">
                      {ctx.ticket_prices.map(p => (
                        <button key={p.label} className="sc-ticket-btn" disabled={busy} onClick={() => addTicket(p.label)}>
                          <strong>{p.label}</strong>
                          <span>{p.pence === 0 ? 'free' : gbp(p.pence)}</span>
                          {tally?.tickets?.[p.label] ? <em>{tally.tickets[p.label]}</em> : null}
                        </button>
                      ))}
                    </div>
                    {lastTicket && ( /* UC-11: only once a ticket has been sold on this phone */
                      <button className="btn btn-ghost btn-sm" disabled={busy} onClick={undoTicket} style={{ marginTop: 8 }}>
                        <Undo2 size={14} /> Undo last ticket
                      </button>
                    )}
                  </div>
                )}

                <div className="card sc-find">
                  {!findOpen ? (
                    <button className="btn btn-secondary" style={{ width: '100%', justifyContent: 'center' }} onClick={() => setFindOpen(true)}>
                      <Search size={15} /> Find by name (forgotten phone)
                    </button>
                  ) : (
                    <>
                      <div className="sc-section-head"><Search size={15} /> Find a club member by surname</div>
                      <input
                        className="input"
                        autoFocus
                        placeholder="Surname…"
                        value={findQuery}
                        onChange={e => setFindQuery(e.target.value)}
                      />
                      <div className="sc-find-results">
                        {findResults === null && findQuery.trim().length >= 2 && <div className="sc-muted">Searching…</div>}
                        {Array.isArray(findResults) && findResults.length === 0 && <div className="sc-muted">No current club member with that name.</div>}
                        {Array.isArray(findResults) && findResults.length > 10 && <div className="sc-muted">More than ten match — add a first name (e.g. "Jo Merrick").</div>}
                        {Array.isArray(findResults) && findResults.slice(0, 10).map(m => (
                          <button key={m.club_member_id} className="sc-find-row" disabled={busy} onClick={() => admitByName(m)}>
                            <Users size={15} /> {m.first_name} {m.last_name}
                            <span>Admit</span>
                          </button>
                        ))}
                      </div>
                      <button className="btn btn-ghost btn-sm" onClick={() => { setFindOpen(false); setFindQuery(''); setFindResults(null); }}>Cancel</button>
                    </>
                  )}
                </div>
              </>
            )}

            {/* Tally strip */}
            {tally && (
              <div className="sc-tally">
                {mode === 'gate' ? (
                  <>
                    <span><strong>{tally.members_in}</strong> club members in</span>
                    {Object.entries(tally.tickets || {}).map(([label, n]) => <span key={label}><strong>{n}</strong> {label}</span>)}
                    <span><strong>{gbp(tally.gate_pence)}</strong> gate</span>
                  </>
                ) : (
                  <span><strong>{tally.bar_visits}</strong> club-member visits today</span>
                )}
              </div>
            )}
            {isAdmin && (
              <p className="sc-muted" style={{ textAlign: 'center', marginTop: 12 }}>
                Totals per fixture are under <Link to={`/${clubSlug}/admin?tab=exports`}>Admin → Reports</Link>.
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ScanCardsPage;
